import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { useAuth } from '@/providers/auth.provider'
import { getRolePermission } from '@/transform/permission.transform'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useQuery } from 'react-query'
import type {
  PermissionRequest,
  RolePermissionRecord,
} from './rolePermission.types'
import { isPermissionAllowed } from './rolePermission.utils'

type RolePermissionContextValue = {
  can: (request: PermissionRequest) => boolean
  isLoading: boolean
}

type RolePermissionGuardProps = PropsWithChildren<
  PermissionRequest & {
    fallback?: ReactNode
  }
>

const RolePermissionContext = createContext<RolePermissionContextValue | null>(
  null
)

function getUserRoleCode(user: any) {
  return (
    user?.subscription?.role_access?.role ??
    user?.role_access?.role ??
    user?.subscriptions?.[0]?.role_access?.role
  )
}

export function RolePermissionProvider({ children }: PropsWithChildren<{}>) {
  const { user } = useAuth()
  const roleCode = getUserRoleCode(user)
  const companyId = user?.company?.id
  const isAdmin = roleCode?.trim().toLowerCase() === 'admin'
  const permissionQuery = useQuery<RolePermissionRecord[]>(
    'GET_ROLE_PERMISSION',
    getRolePermission,
    {
      cacheTime: 0,
      enabled: !isAdmin,
      refetchOnMount: 'always',
      staleTime: 0,
    }
  )
  const permissions = permissionQuery.data ?? []
  const can = useCallback(
    (request: PermissionRequest) =>
      isAdmin ||
      isPermissionAllowed({
        ...request,
        companyId,
        permissions,
        roleCode,
      }),
    [companyId, isAdmin, permissions, roleCode]
  )
  const value = useMemo(
    () => ({
      can,
      isLoading: !isAdmin && permissionQuery.isLoading,
    }),
    [can, isAdmin, permissionQuery.isLoading]
  )

  return (
    <RolePermissionContext.Provider value={value}>
      {children}
    </RolePermissionContext.Provider>
  )
}

export function useRolePermission() {
  const context = useContext(RolePermissionContext)

  if (!context) {
    throw new Error(
      'useRolePermission must be used within RolePermissionProvider'
    )
  }

  return context
}

export function RolePermissionGuard({
  action,
  children,
  fallback = null,
  module,
  resourceScopes,
}: RolePermissionGuardProps) {
  const { can, isLoading } = useRolePermission()

  if (isLoading) return <LoadingComponent />

  return can({ action, module, resourceScopes }) ? (
    <>{children}</>
  ) : (
    <>{fallback}</>
  )
}
